import React, { useEffect, useState } from 'react';
import api from '../api/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { ItemsEditor, VatTotals, emptyItem, itemsInvalid, cleanItems } from './QuoteItemsEditor';

const inDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

const PaymentFields = ({ payment, setPayment }) => {
  const set = (k) => (e) => setPayment({ ...payment, [k]: e.target ? e.target.value : e });
  return (
    <div className="space-y-2 rounded-xl border border-[#E3F2FD] p-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Come vuoi essere pagato</div>
      <div className="grid sm:grid-cols-2 gap-2">
        <Input placeholder="IBAN" value={payment.iban} onChange={set('iban')} data-testid="quote-iban-input" />
        <Input placeholder="Intestatario conto" value={payment.iban_holder} onChange={set('iban_holder')} data-testid="quote-iban-holder-input" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Select value={payment.link_type} onValueChange={set('link_type')}>
          <SelectTrigger data-testid="quote-link-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="stripe">Stripe</SelectItem>
            <SelectItem value="paypal">PayPal</SelectItem>
            <SelectItem value="altro">Altro</SelectItem>
          </SelectContent>
        </Select>
        <Input className="col-span-2" placeholder="https://… link di pagamento" value={payment.link} onChange={set('link')} data-testid="quote-link-input" />
      </div>
    </div>
  );
};

const TemplatePicker = ({ templates, onPick }) => {
  const [value, setValue] = useState('');
  if (!templates.length) return null;
  return (
    <div className="space-y-1">
      <Label>Parti da un modello</Label>
      <Select value={value} onValueChange={(id) => { onPick(templates.find((t) => t.template_id === id)); setValue(''); }}>
        <SelectTrigger data-testid="quote-template-select"><SelectValue placeholder="Scegli un modello (facoltativo)" /></SelectTrigger>
        <SelectContent>
          {templates.map((t) => (
            <SelectItem key={t.template_id} value={t.template_id} data-testid={`quote-template-option-${t.template_id}`}>{t.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

const QuoteFormDialog = ({ open, onOpenChange, onCreated, presetRequestId }) => {
  const [requests, setRequests] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [requestId, setRequestId] = useState('');
  const [items, setItems] = useState([emptyItem()]);
  const [vatEnabled, setVatEnabled] = useState(false);
  const [vatRate, setVatRate] = useState(22);
  const [notes, setNotes] = useState('');
  const [validUntil, setValidUntil] = useState(inDays(15));
  const [payment, setPayment] = useState({ iban: '', iban_holder: '', link: '', link_type: 'stripe' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setItems([emptyItem()]); setNotes(''); setVatEnabled(false); setVatRate(22); setValidUntil(inDays(15));
    api.get('/api/quotes/templates').then((r) => setTemplates(r.data.items || [])).catch(() => setTemplates([]));
    api.get('/api/quotes/eligible-requests').then((r) => {
      setRequests(r.data.items || []);
      setPayment((p) => ({ ...p, ...(r.data.payment_defaults || {}) }));
      setRequestId(presetRequestId || r.data.items?.[0]?.request_id || '');
    }).catch(() => toast.error('Errore nel caricamento delle richieste'));
  }, [open, presetRequestId]);

  const applyTemplate = (t) => {
    if (!t) return;
    setItems(t.items.map((i) => ({ ...i })));
    setVatEnabled(!!t.vat_enabled); setVatRate(t.vat_rate ?? 22);
    setNotes(t.notes || ''); setValidUntil(inDays(t.valid_days || 15));
    toast.success(`Modello «${t.name}» applicato: puoi modificarlo prima dell'invio`);
  };

  const submit = async () => {
    if (!requestId) return toast.error('Seleziona la richiesta');
    if (itemsInvalid(items)) return toast.error('Completa tutte le voci');
    setSaving(true);
    try {
      await api.post('/api/quotes', {
        request_id: requestId, notes, valid_until: validUntil, vat_enabled: vatEnabled, vat_rate: Number(vatRate || 0), payment,
        items: cleanItems(items),
      });
      toast.success('Preventivo inviato al cliente');
      onOpenChange(false); onCreated?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Controlla i dati del preventivo');
    } finally { setSaving(false); }
  };

  const presetMissing = presetRequestId && requests.length > 0 && !requests.some((r) => r.request_id === presetRequestId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-form-dialog">
        <DialogHeader>
          <DialogTitle>Nuovo preventivo</DialogTitle>
          <DialogDescription>Il cliente lo riceverà in piattaforma, in chat e via email con il PDF.</DialogDescription>
        </DialogHeader>
        {requests.length === 0 || presetMissing ? (
          <p className="text-sm text-slate-600" data-testid="quote-no-requests">Nessuna richiesta accettata e attiva: accetta prima una richiesta per inviare un preventivo.</p>
        ) : (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Richiesta</Label>
                <Select value={requestId} onValueChange={setRequestId} disabled={!!presetRequestId}>
                  <SelectTrigger data-testid="quote-request-select"><SelectValue placeholder="Seleziona la richiesta" /></SelectTrigger>
                  <SelectContent>
                    {requests.map((r) => (
                      <SelectItem key={r.request_id} value={r.request_id} data-testid={`quote-request-option-${r.request_id}`}>
                        {r.client_name} · {r.service}{r.comune ? ` · ${r.comune}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <TemplatePicker templates={templates} onPick={applyTemplate} />
            </div>
            <ItemsEditor items={items} setItems={setItems} />
            <VatTotals items={items} vatEnabled={vatEnabled} setVatEnabled={setVatEnabled} vatRate={vatRate} setVatRate={setVatRate} />
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Valido fino al</Label>
                <Input type="date" min={inDays(0)} value={validUntil} onChange={(e) => setValidUntil(e.target.value)} data-testid="quote-valid-until-input" />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label>Note</Label>
                <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Condizioni, tempi, cosa è incluso…" data-testid="quote-notes-input" />
              </div>
            </div>
            <PaymentFields payment={payment} setPayment={setPayment} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="quote-cancel-form-btn">Annulla</Button>
              <Button onClick={submit} disabled={saving} data-testid="quote-submit-btn">{saving ? 'Invio…' : 'Invia preventivo'}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuoteFormDialog;
